/**
 * Color scheme configuration for PrimeNG preset
 * Uses Figma semantic & component tokens — no PrimeNG '{reference}' strings
 */

import { createInputTokens, createModalTokens, inputTokens, modalTokens } from '../tokens/components.js';
import * as p from '../tokens/primitives.js';
import { border, interactive, surface, text, type SemanticTokens } from '../tokens/semantic.js';

export const colorScheme = {
  // Color scheme configuration
  colorScheme: {
    light: {
      surface: {
        // Numeric shade scale (PrimeNG internals) — mapped via primitives
        0: p.neutral.white,
        50: p.neutral[50],
        100: p.neutral[50],
        200: p.neutral[50],
        300: p.neutral[200],
        400: p.neutral[600],
        500: p.neutral[700],
        600: p.neutral[800],
        700: p.neutral[800],
        800: p.neutral[800],
        900: p.neutral[900],
        950: p.neutral[950],
        // Semantic surface properties
        ground: surface.ground,
        canvas: surface.canvas,
        raised: surface.raised,
        modal: surface.modal,
        disabled: surface.disabled,
        overlay: 'rgba(8, 24, 58, 0.5)',
        white: p.neutral.white,
      },
      primary: {
        color: interactive.primary.base,
        contrastColor: surface.ground,
        hoverColor: interactive.primary.hover,
        activeColor: interactive.primary.pressed,
      },
      highlight: {
        background: interactive.primary.subtle,
        focusBackground: interactive.primary.subtlePressed,
        color: interactive.primary.base,
        focusColor: interactive.primary.base,
      },
      focusRing: {
        shadow: `0 0 0 0.2rem ${interactive.primary.subtle}`,
      },
      mask: {
        background: 'rgba(8, 24, 58, 0.5)',
        color: text.secondary,
      },
      dialog: {
        background: modalTokens.dialogBg,
        borderColor: modalTokens.dialogBorder,
        color: text.secondary,
        borderRadius: modalTokens.base.radius,
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      formField: {
        background: '',
        disabledBackground: inputTokens.bgDisabled,
        filledBackground: surface.canvas,
        filledHoverBackground: surface.canvas,
        filledFocusBackground: '',
        borderColor: inputTokens.border,
        hoverBorderColor: border.strong,
        focusBorderColor: inputTokens.borderFocus,
        invalidBorderColor: border.danger,
        color: inputTokens.fg,
        disabledColor: text.disabled,
        placeholderColor: inputTokens.placeholder,
        invalidPlaceholderColor: text.danger,
        floatLabelColor: text.muted,
        floatLabelFocusColor: text.primary,
        floatLabelActiveColor: text.muted,
        floatLabelInvalidColor: text.danger,
        iconColor: text.muted,
        shadow: 'none',
      },
      text: {
        color: text.secondary,
        hoverColor: text.primary,
        mutedColor: text.muted,
        hoverMutedColor: text.tertiary,
      },
      content: {
        background: surface.ground,
        hoverBackground: surface.canvas,
        borderColor: border.divider,
        color: text.secondary,
        hoverColor: text.primary,
      },
      overlay: {
        select: {
          background: surface.ground,
          borderColor: border.divider,
          color: text.secondary,
        },
        popover: {
          background: surface.ground,
          borderColor: border.divider,
          color: text.secondary,
        },
        modal: {
          background: modalTokens.dialogBg,
          borderColor: modalTokens.dialogBorder,
          color: text.secondary,
        },
      },
      list: {
        option: {
          focusBackground: surface.canvas,
          selectedBackground: interactive.primary.subtle,
          selectedFocusBackground: interactive.primary.subtlePressed,
          color: text.secondary,
          focusColor: text.primary,
          selectedColor: text.primary,
          selectedFocusColor: text.primary,
          icon: {
            color: text.muted,
            focusColor: text.tertiary,
          },
        },
        optionGroup: {
          background: 'transparent',
          color: text.secondary,
        },
      },
      navigation: {
        item: {
          focusBackground: surface.canvas,
          activeBackground: surface.canvas,
          color: text.secondary,
          focusColor: text.primary,
          activeColor: text.primary,
          icon: {
            color: text.muted,
            focusColor: text.tertiary,
            activeColor: text.tertiary,
          },
        },
        submenuLabel: {
          background: 'transparent',
          color: text.secondary,
        },
        submenuIcon: {
          color: text.muted,
          focusColor: text.tertiary,
          activeColor: text.tertiary,
        },
      },
    },
    dark: {
      surface: {
        // Numeric shade scale (PrimeNG internals) — mapped via primitives
        0: p.neutral.white,
        50: p.neutral[50],
        100: p.neutral[50],
        200: p.neutral[50],
        300: p.neutral[200],
        400: p.neutral[600],
        500: p.neutral[700],
        600: p.neutral[800],
        700: p.neutral[800],
        800: p.neutral[800],
        900: p.neutral[900],
        950: p.neutral[950],
        // Semantic surface properties
        ground: surface.ground,
        canvas: surface.canvas,
        raised: surface.raised,
        modal: surface.modal,
        disabled: surface.disabled,
        overlay: 'rgba(8, 24, 58, 0.5)',
        white: p.neutral.white,
      },
      primary: {
        color: interactive.primary.base,
        contrastColor: text.onColor,
        hoverColor: interactive.primary.hover,
        activeColor: interactive.primary.pressed,
      },
      highlight: {
        background: interactive.primary.subtle,
        focusBackground: interactive.primary.subtlePressed,
        color: interactive.primary.base,
        focusColor: interactive.primary.base,
      },
      focusRing: {
        shadow: `0 0 0 0.2rem ${interactive.primary.subtle}`,
      },
      mask: {
        background: 'rgba(8, 24, 58, 0.5)',
        color: text.secondary,
      },
      dialog: {
        background: modalTokens.dialogBg,
        borderColor: modalTokens.dialogBorder,
        color: text.secondary,
        borderRadius: modalTokens.base.radius,
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      formField: {
        background: surface.ground,
        disabledBackground: inputTokens.bgDisabled,
        filledBackground: surface.canvas,
        filledHoverBackground: surface.canvas,
        filledFocusBackground: '',
        borderColor: inputTokens.border,
        hoverBorderColor: border.strong,
        focusBorderColor: inputTokens.borderFocus,
        invalidBorderColor: border.danger,
        color: inputTokens.fg,
        disabledColor: text.disabled,
        placeholderColor: inputTokens.placeholder,
        invalidPlaceholderColor: text.danger,
        floatLabelColor: text.muted,
        floatLabelFocusColor: text.primary,
        floatLabelActiveColor: text.muted,
        floatLabelInvalidColor: text.danger,
        iconColor: text.muted,
        shadow: 'none',
      },
      text: {
        color: text.secondary,
        hoverColor: text.primary,
        mutedColor: text.muted,
        hoverMutedColor: text.tertiary,
      },
      content: {
        background: surface.ground,
        hoverBackground: surface.canvas,
        borderColor: border.divider,
        color: text.secondary,
        hoverColor: text.primary,
      },
      overlay: {
        select: {
          background: surface.ground,
          borderColor: border.divider,
          color: text.secondary,
        },
        popover: {
          background: surface.ground,
          borderColor: border.divider,
          color: text.secondary,
        },
        modal: {
          background: modalTokens.dialogBg,
          borderColor: modalTokens.dialogBorder,
          color: text.secondary,
        },
      },
      list: {
        option: {
          focusBackground: surface.canvas,
          selectedBackground: interactive.primary.subtle,
          selectedFocusBackground: interactive.primary.subtlePressed,
          color: text.secondary,
          focusColor: text.primary,
          selectedColor: text.primary,
          selectedFocusColor: text.primary,
          icon: {
            color: text.muted,
            focusColor: text.tertiary,
          },
        },
        optionGroup: {
          background: 'transparent',
          color: text.secondary,
        },
      },
      navigation: {
        item: {
          focusBackground: surface.canvas,
          activeBackground: surface.canvas,
          color: text.secondary,
          focusColor: text.primary,
          activeColor: text.primary,
          icon: {
            color: text.muted,
            focusColor: text.tertiary,
            activeColor: text.tertiary,
          },
        },
        submenuLabel: {
          background: 'transparent',
          color: text.secondary,
        },
        submenuIcon: {
          color: text.muted,
          focusColor: text.tertiary,
          activeColor: text.tertiary,
        },
      },
    },
  },
};





export function createColorScheme(
  s: SemanticTokens,
  it: ReturnType<typeof createInputTokens>,
  mt: ReturnType<typeof createModalTokens>
) {
  return {
    colorScheme: {
      light: {
        surface: {
          0: p.neutral.white,
          50: p.neutral[50],
          100: p.neutral[50],
          200: p.neutral[50],
          300: p.neutral[200],
          400: p.neutral[600],
          500: p.neutral[700],
          600: p.neutral[800],
          700: p.neutral[800],
          800: p.neutral[800],
          900: p.neutral[900],
          950: p.neutral[950],
          ground: s.surface.ground,
          canvas: s.surface.canvas,
          raised: s.surface.raised,
          modal: s.surface.modal,
          disabled: s.surface.disabled,
          overlay: 'rgba(8, 24, 58, 0.5)',
          white: p.neutral.white,
        },
        primary: {
          color: s.interactive.primary.base,
          contrastColor: s.surface.ground,
          hoverColor: s.interactive.primary.hover,
          activeColor: s.interactive.primary.pressed,
        },
        highlight: {
          background: s.interactive.primary.subtle,
          focusBackground: s.interactive.primary.subtlePressed,
          color: s.interactive.primary.base,
          focusColor: s.interactive.primary.base,
        },
        focusRing: {
          shadow: `0 0 0 0.2rem ${s.interactive.primary.subtle}`,
        },
        mask: {
          background: 'rgba(8, 24, 58, 0.5)',
          color: s.text.secondary,
        },
        dialog: {
          background: mt.dialogBg,
          borderColor: mt.dialogBorder,
          color: s.text.secondary,
          borderRadius: mt.base.radius,
          shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        },
        formField: {
          background: '',
          disabledBackground: it.bgDisabled,
          filledBackground: s.surface.canvas,
          filledHoverBackground: s.surface.canvas,
          filledFocusBackground: '',
          borderColor: it.border,
          hoverBorderColor: s.border.strong,
          focusBorderColor: it.borderFocus,
          invalidBorderColor: s.border.danger,
          color: it.fg,
          disabledColor: s.text.disabled,
          placeholderColor: it.placeholder,
          invalidPlaceholderColor: s.text.danger,
          floatLabelColor: s.text.muted,
          floatLabelFocusColor: s.text.primary,
          floatLabelActiveColor: s.text.muted,
          floatLabelInvalidColor: s.text.danger,
          iconColor: s.text.muted,
          shadow: 'none',
        },
        text: {
          color: s.text.secondary,
          hoverColor: s.text.primary,
          mutedColor: s.text.muted,
          hoverMutedColor: s.text.tertiary,
        },
        content: {
          background: s.surface.ground,
          hoverBackground: s.surface.canvas,
          borderColor: s.border.divider,
          color: s.text.secondary,
          hoverColor: s.text.primary,
        },
        overlay: {
          select: {
            background: s.surface.ground,
            borderColor: s.border.divider,
            color: s.text.secondary,
          },
          popover: {
            background: s.surface.ground,
            borderColor: s.border.divider,
            color: s.text.secondary,
          },
          modal: {
            background: mt.dialogBg,
            borderColor: mt.dialogBorder,
            color: s.text.secondary,
          },
        },
        list: {
          option: {
            focusBackground: s.surface.canvas,
            selectedBackground: s.interactive.primary.subtle,
            selectedFocusBackground: s.interactive.primary.subtlePressed,
            color: s.text.secondary,
            focusColor: s.text.primary,
            selectedColor: s.text.primary,
            selectedFocusColor: s.text.primary,
            icon: {
              color: s.text.muted,
              focusColor: s.text.tertiary,
            },
          },
          optionGroup: {
            background: 'transparent',
            color: s.text.secondary,
          },
        },
        navigation: {
          item: {
            focusBackground: s.surface.canvas,
            activeBackground: s.surface.canvas,
            color: s.text.secondary,
            focusColor: s.text.primary,
            activeColor: s.text.primary,
            icon: {
              color: s.text.muted,
              focusColor: s.text.tertiary,
              activeColor: s.text.tertiary,
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: s.text.secondary,
          },
          submenuIcon: {
            color: s.text.muted,
            focusColor: s.text.tertiary,
            activeColor: s.text.tertiary,
          },
        },
      },
      dark: {
        surface: {
          0: p.neutral.white,
          50: p.neutral[50],
          100: p.neutral[50],
          200: p.neutral[50],
          300: p.neutral[200],
          400: p.neutral[600],
          500: p.neutral[700],
          600: p.neutral[800],
          700: p.neutral[800],
          800: p.neutral[800],
          900: p.neutral[900],
          950: p.neutral[950],
          ground: s.surface.ground,
          canvas: s.surface.canvas,
          raised: s.surface.raised,
          modal: s.surface.modal,
          disabled: s.surface.disabled,
          overlay: 'rgba(8, 24, 58, 0.5)',
          white: p.neutral.white,
        },
        primary: {
          color: s.interactive.primary.base,
          contrastColor: s.text.onColor,
          hoverColor: s.interactive.primary.hover,
          activeColor: s.interactive.primary.pressed,
        },
        highlight: {
          background: s.interactive.primary.subtle,
          focusBackground: s.interactive.primary.subtlePressed,
          color: s.interactive.primary.base,
          focusColor: s.interactive.primary.base,
        },
        focusRing: {
          shadow: `0 0 0 0.2rem ${s.interactive.primary.subtle}`,
        },
        mask: {
          background: 'rgba(8, 24, 58, 0.5)',
          color: s.text.secondary,
        },
        dialog: {
          background: mt.dialogBg,
          borderColor: mt.dialogBorder,
          color: s.text.secondary,
          borderRadius: mt.base.radius,
          shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        },
        formField: {
          background: s.surface.ground,
          disabledBackground: it.bgDisabled,
          filledBackground: s.surface.canvas,
          filledHoverBackground: s.surface.canvas,
          filledFocusBackground: '',
          borderColor: it.border,
          hoverBorderColor: s.border.strong,
          focusBorderColor: it.borderFocus,
          invalidBorderColor: s.border.danger,
          color: it.fg,
          disabledColor: s.text.disabled,
          placeholderColor: it.placeholder,
          invalidPlaceholderColor: s.text.danger,
          floatLabelColor: s.text.muted,
          floatLabelFocusColor: s.text.primary,
          floatLabelActiveColor: s.text.muted,
          floatLabelInvalidColor: s.text.danger,
          iconColor: s.text.muted,
          shadow: 'none',
        },
        text: {
          color: s.text.secondary,
          hoverColor: s.text.primary,
          mutedColor: s.text.muted,
          hoverMutedColor: s.text.tertiary,
        },
        content: {
          background: s.surface.ground,
          hoverBackground: s.surface.canvas,
          borderColor: s.border.divider,
          color: s.text.secondary,
          hoverColor: s.text.primary,
        },
        overlay: {
          select: {
            background: s.surface.ground,
            borderColor: s.border.divider,
            color: s.text.secondary,
          },
          popover: {
            background: s.surface.ground,
            borderColor: s.border.divider,
            color: s.text.secondary,
          },
          modal: {
            background: mt.dialogBg,
            borderColor: mt.dialogBorder,
            color: s.text.secondary,
          },
        },
        list: {
          option: {
            focusBackground: s.surface.canvas,
            selectedBackground: s.interactive.primary.subtle,
            selectedFocusBackground: s.interactive.primary.subtlePressed,
            color: s.text.secondary,
            focusColor: s.text.primary,
            selectedColor: s.text.primary,
            selectedFocusColor: s.text.primary,
            icon: {
              color: s.text.muted,
              focusColor: s.text.tertiary,
            },
          },
          optionGroup: {
            background: 'transparent',
            color: s.text.secondary,
          },
        },
        navigation: {
          item: {
            focusBackground: s.surface.canvas,
            activeBackground: s.surface.canvas,
            color: s.text.secondary,
            focusColor: s.text.primary,
            activeColor: s.text.primary,
            icon: {
              color: s.text.muted,
              focusColor: s.text.tertiary,
              activeColor: s.text.tertiary,
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: s.text.secondary,
          },
          submenuIcon: {
            color: s.text.muted,
            focusColor: s.text.tertiary,
            activeColor: s.text.tertiary,
          },
        },
      },
    },
  };
}
